/**
 * Cihaz kartları — `deviceInfo.cards[]` panel tarafı tek kaynağı.
 *
 * Sözleşme: BayinerIOT-Workspace- `docs/api-contracts/device-info-cards.md`
 * (bayineras/BayinerIOT-Workspace-#38).
 *
 * Kart = { type, port, model, version }
 *   type    : CARD_TYPE_ORDER'daki değerlerden biri (bilinmeyen tip KORUNUR)
 *   port    : anakart 0, askı 1-3, değirmen 4
 *   model   : kartın BİLDİRDİĞİ model no; bildirmediyse null
 *   version : öneksiz "x.y.z"; bilinmiyorsa null
 *
 * NEDEN AYNI KURAL: server `src/modules/device/domain/deviceCards.js` aynı
 * normalleştirme + türetme kuralını uygular. Deploy sonrası hiç deviceInfo
 * göndermemiş cihazların kaydında `cards` yoktur; panel onları eski düz
 * alanlardan türetir. Biri değişirse diğeri de değişmeli — aksi halde panel
 * ile "Cihaz Genel" raporu aynı cihaz için farklı kart listesi gösterir.
 *
 * Neden versionUtils.normalizeVersion kullanılmıyor: o fonksiyon ham metni
 * KORUR ("CHA1.0.1" döner, filtre karşılaştırması için). Burada server ile
 * birebir aynı "ilk x.y.z sayı grubu" çıkarımı gerekiyor.
 */

export const CARD_TYPE_ORDER = [
  "caysetMainBoard",
  "fincandaMainBoard",
  "fincandaHanger",
  "fincandaGrinder",
  // Eski kart seti (OKC): ekran ve 4G kartı (WS#402)
  "fincandaLcd",
  "fincandaGateway",
];

const HANGER_COUNT = 3;
const MAIN_BOARD_PORT = 0;
const GRINDER_PORT = 4;

const MAIN_BOARD_TYPES = new Set(["caysetMainBoard", "fincandaMainBoard"]);

/**
 * Metindeki ilk "x.y.z" (veya "x.y") sayı grubunu döndürür; önekler atılır.
 * "CHA1.0.1" → "1.0.1", "C2.0.3" → "2.0.3", "T3.1" → "3.1", "" → null.
 */
export const normalizeCardVersion = (value) => {
  if (value === undefined || value === null) return null;
  const match = String(value).match(/\d+(?:\.\d+){1,2}/);
  return match ? match[0] : null;
};

export const normalizeCardModel = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

const typeRank = (type) => {
  const i = CARD_TYPE_ORDER.indexOf(type);
  // Bilinmeyen tipler bilinenlerden sonra, kendi aralarında ada göre
  return i === -1 ? CARD_TYPE_ORDER.length : i;
};

const compareCards = (a, b) => {
  const r = typeRank(a.type) - typeRank(b.type);
  if (r !== 0) return r;
  if (a.type !== b.type) return a.type < b.type ? -1 : 1;
  return a.port - b.port;
};

/**
 * `cards` dizisini doğrular, normalleştirir ve sözleşme sırasına dizer.
 * Geçersiz eleman atılır; aynı (type, port) ikinci kez gelirse SON gelen
 * kazanır. Hem modeli hem sürümü boş kart atılır (yalnız var olan kartlar).
 */
export const sanitizeCards = (input) => {
  if (!Array.isArray(input)) return [];
  const byKey = new Map();
  input.forEach((raw) => {
    if (!raw || typeof raw !== "object") return;
    const type = typeof raw.type === "string" ? raw.type.trim() : "";
    const port = Number(raw.port);
    if (!type || !Number.isInteger(port) || port < 0) return;
    const card = {
      type,
      port,
      model: normalizeCardModel(raw.model),
      version: normalizeCardVersion(raw.version),
    };
    if (card.model === null && card.version === null) return;
    byKey.set(`${type}:${port}`, card);
  });
  return [...byKey.values()].sort(compareCards);
};

/**
 * Eski düz sürüm alanlarından kart listesi türetir (model: null).
 * Sözleşmedeki "Türetme kuralı" bölümünün birebir karşılığı.
 */
export const deriveCardsFromLegacy = (info = {}) => {
  const cards = [];
  const push = (type, port, rawVersion) => {
    const version = normalizeCardVersion(rawVersion);
    if (version) cards.push({ type, port, model: null, version });
  };

  push("caysetMainBoard", MAIN_BOARD_PORT, info.caysetVersion);
  push("fincandaMainBoard", MAIN_BOARD_PORT, info.mainboardVersion);

  // İki anakart alanı da boşsa hwVersion'dan ("T3.1.3 / C2.0.3") çıkar.
  // "CG"/"CH" önekleri değirmen/askıdır, anakart değil.
  if (
    !normalizeCardVersion(info.caysetVersion) &&
    !normalizeCardVersion(info.mainboardVersion) &&
    typeof info.hwVersion === "string"
  ) {
    info.hwVersion.split("/").forEach((part) => {
      const text = part.trim();
      if (/^T\d/i.test(text)) push("caysetMainBoard", MAIN_BOARD_PORT, text);
      else if (/^C\d/i.test(text)) push("fincandaMainBoard", MAIN_BOARD_PORT, text);
    });
  }

  const hangers = Array.isArray(info.hangerVersions) ? info.hangerVersions : [];
  for (let i = 0; i < HANGER_COUNT; i += 1) {
    push("fincandaHanger", i + 1, hangers[i]);
  }

  push("fincandaGrinder", GRINDER_PORT, info.grinderVersion);

  return sanitizeCards(cards);
};

/** Okuma: `cards` doluysa o (normalleştirilmiş), değilse türetilmiş. */
export const getDeviceCards = (deviceInfo) => {
  if (!deviceInfo || typeof deviceInfo !== "object") return [];
  if (Array.isArray(deviceInfo.cards) && deviceInfo.cards.length > 0) {
    return sanitizeCards(deviceInfo.cards);
  }
  return deriveCardsFromLegacy(deviceInfo);
};

// ─── Gösterim ───────────────────────────────────────────────────────────────

const CARD_TYPE_LABELS = {
  caysetMainBoard: "Çayset Anakart",
  fincandaMainBoard: "Fincanda Anakart",
  fincandaHanger: "Fincanda Askı",
  fincandaGrinder: "Fincanda Değirmen",
  fincandaLcd: "Fincanda LCD Kartı",
  fincandaGateway: "Fincanda 4G Kartı",
};

/** Bilinmeyen tip atlanmaz — ham adıyla gösterilir. */
export const getCardTypeLabel = (type) => CARD_TYPE_LABELS[type] || type || "—";

const MODEL_VARIANT_LABELS = {
  22: "2'li",
  24: "4'lü",
  26: "6'lı",
  40: "J18A",
  41: "G18A",
};

/** 24 → "24 (4'lü)", 10 → "10", null → "Bilinmiyor". */
export const getCardModelLabel = (model) => {
  const n = normalizeCardModel(model);
  if (n === null) return "Bilinmiyor";
  const variant = MODEL_VARIANT_LABELS[n];
  return variant ? `${n} (${variant})` : String(n);
};

/**
 * Kısa model etiketi (rapor hücresi / filtre özeti): 41 → "41 G18A",
 * 10 → "10", null → null. Server domain/deviceCards.js ile AYNI metin.
 */
export const getCardModelShortLabel = (model) => {
  const n = normalizeCardModel(model);
  if (n === null) return null;
  const variant = MODEL_VARIANT_LABELS[n];
  return variant ? `${n} ${variant}` : String(n);
};

/**
 * Rapor hücresi: sürüm + model yan yana, sıkışık.
 *   { version: "1.2.2", model: 41 }   → "1.2.2 (41 G18A)"
 *   { version: "1.1.0", model: null } → "1.1.0"
 *   { version: null, model: 24 }      → "– (24 4'lü)"
 *   ikisi de yok                      → null
 */
export const formatCardVersionWithModel = (card) => {
  if (!card) return null;
  const version = normalizeCardVersion(card.version);
  const model = getCardModelShortLabel(card.model);
  if (!model) return version;
  return `${version || "–"} (${model})`;
};

/** Konum yalnız askıda anlamlı (1-3); anakart ve değirmende "–". */
export const getCardPortLabel = (card) =>
  card?.type === "fincandaHanger" ? String(card.port) : "–";

const MAIN_BOARD_PREFIX = {
  caysetMainBoard: "T",
  fincandaMainBoard: "C",
};

/**
 * Listelerdeki "Kart" rozeti: anakart sürümleri bugünkü görünümle
 * ("T3.1.3 / C2.0.3"). Anakart kartı yoksa hwVersion'a düşer.
 */
export const formatMainBoardVersions = (cards, fallbackHwVersion) => {
  const parts = (Array.isArray(cards) ? cards : [])
    .filter((card) => MAIN_BOARD_TYPES.has(card?.type) && card.version)
    .map((card) => `${MAIN_BOARD_PREFIX[card.type]}${card.version}`);
  if (parts.length > 0) return parts.join(" / ");
  return fallbackHwVersion || "";
};

/** Kısayol: deviceInfo'dan doğrudan liste rozeti metni. */
export const getMainBoardVersionText = (deviceInfo) =>
  formatMainBoardVersions(getDeviceCards(deviceInfo), deviceInfo?.hwVersion);
